import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    rolle: '',
    name: '',
    email: '',
    telefon: '',
    nachricht: '',
    consent_contact: false,
    consent_marketing: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.consent_contact) {
      toast.error('Bitte bestätigen Sie, dass wir Sie kontaktieren dürfen.');
      return;
    }

    if (!formData.rolle || !formData.name || !formData.email) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    setLoading(true);
    try {
      const leadResponse = await axios.post(`${API}/leads`, {
        ...formData,
        quelle: 'Kontaktformular',
      });
      
      // If marketing consent given, trigger DOI flow
      if (formData.consent_marketing) {
        try {
          await axios.post(`${API}/doi/send`, {
            email: formData.email,
            lead_id: leadResponse.data.lead_id || null
          });
          toast.success('Vielen Dank! Bitte bestätigen Sie Ihre E-Mail-Adresse, um Zugang zu unseren Lead Magnets zu erhalten.', {
            duration: 6000
          });
        } catch (doiError) {
          console.error('DOI send error:', doiError);
          // Don't show error to user, just log it - lead was captured successfully
          toast.success('Vielen Dank! Wir melden uns zeitnah bei Ihnen.');
        }
      } else {
        toast.success('Vielen Dank! Wir melden uns zeitnah bei Ihnen.');
      }
      
      // Reset form
      setFormData({
        rolle: '',
        name: '',
        email: '',
        telefon: '',
        nachricht: '',
        consent_contact: false,
        consent_marketing: false,
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Senden des Formulars');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Kontakt – Handelsimmobilie bewerten lassen"
        description="Vereinbaren Sie ein unverbindliches Erstgespräch für den Verkauf Ihrer Handelsimmobilie. Kostenlose Ersteinschätzung und professionelle Beratung."
        url="/kontakt"
      />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1
              className="text-4xl md:text-5xl font-semibold mb-4"
              style={{ color: 'var(--primary-800)' }}
            >
              Kontakt
            </h1>
            <p className="text-lg" style={{ color: 'var(--secondary-600)' }}>
              Vereinbaren Sie ein unverbindliches Erstgespräch
            </p>
          </div>

          <Card className="p-8">
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <Label htmlFor="rolle">
                    Ich bin... <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.rolle}
                    onValueChange={(value) =>
                      setFormData({ ...formData, rolle: value })
                    }
                  >
                    <SelectTrigger id="rolle" data-testid="rolle-select">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Eigentümer">Eigentümer</SelectItem>
                      <SelectItem value="Investor">Investor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="name">
                    Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Max Mustermann"
                  />
                </div>

                <div>
                  <Label htmlFor="email">
                    E-Mail <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="email-input"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="max@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="telefon">Telefon (optional)</Label>
                  <Input
                    id="telefon"
                    data-testid="telefon-input"
                    value={formData.telefon}
                    onChange={(e) =>
                      setFormData({ ...formData, telefon: e.target.value })
                    }
                    placeholder="+49 123 456789"
                  />
                </div>

                <div>
                  <Label htmlFor="nachricht">Nachricht (optional)</Label>
                  <Textarea
                    id="nachricht"
                    data-testid="nachricht-textarea"
                    value={formData.nachricht}
                    onChange={(e) =>
                      setFormData({ ...formData, nachricht: e.target.value })
                    }
                    placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                    rows={5}
                  />
                </div>

                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="consent_contact"
                      data-testid="consent-contact-checkbox"
                      checked={formData.consent_contact}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, consent_contact: checked })
                      }
                    />
                    <Label
                      htmlFor="consent_contact"
                      className="text-sm cursor-pointer"
                      style={{ color: 'var(--secondary-700)' }}
                    >
                      Ich willige ein, dass RetailInvest mich per E-Mail oder Telefon kontaktieren
                      darf. <span className="text-red-500">*</span>
                    </Label>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="consent_marketing"
                      data-testid="consent-marketing-checkbox"
                      checked={formData.consent_marketing}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, consent_marketing: checked })
                      }
                    />
                    <Label
                      htmlFor="consent_marketing"
                      className="text-sm cursor-pointer"
                      style={{ color: 'var(--secondary-700)' }}
                    >
                      Ich möchte zukünftig über Marktanalysen und Insights informiert werden (optional).
                    </Label>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-contact-button"
                  className="w-full py-6 text-lg font-medium"
                  style={{
                    backgroundColor: 'var(--primary-500)',
                    color: 'white',
                  }}
                >
                  {loading ? 'Wird gesendet...' : 'Anfrage senden'}
                </Button>

                <p className="text-xs text-center" style={{ color: 'var(--secondary-500)' }}>
                  Ihre Daten werden vertraulich behandelt. Weitere Informationen finden Sie in unserer{' '}
                  <a
                    href="/datenschutz"
                    className="underline"
                    style={{ color: 'var(--primary-600)' }}
                  >
                    Datenschutzerklärung
                  </a>
                  .
                </p>
              </div>
            </form>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;